import { imageBucket, avatarBucket } from '$lib/supabase';

// Cache for generated URLs
const urlCache = new Map<string, string>();

// Clear cache function for development
export function clearImageCache() {
	urlCache.clear();
}

// Clear cache on page load to ensure fresh optimized URLs
if (typeof window !== 'undefined') {
	clearImageCache();
}

// Define standard sizes for different devices - optimized for card thumbnails
const SIZES = {
	mobile: 256, // for mobile cards (w-64)
	tablet: 300, // for tablet cards (md:w-[300px])
	desktop: 320 // for desktop cards (2xl:w-[320px])
};

// Helper to calculate 16:9 height from width
function get16by9Height(width: number): number {
	return Math.round((width * 9) / 16);
}

function logImageError(url: string, error: any) {
	console.error('Image Error:', {
		url,
		error,
		timestamp: new Date().toISOString()
	});
}

export function getOptimizedImageUrl(path: string, width: number): string {
	try {
		const height = get16by9Height(width); // Calculate 16:9 height
		const cacheKey = `${path}-${width}x${height}`;

		if (urlCache.has(cacheKey)) {
			return urlCache.get(cacheKey)!;
		}

		// Use the render endpoint for transformations instead of object endpoint
		const {
			data: { publicUrl: baseUrl }
		} = imageBucket('images').getPublicUrl(path, {
			transform: {
				width,
				height, // Specify height for 16:9
				format: 'origin',
				quality: 70, // Reduced quality for thumbnails
				resize: 'cover'
			}
		});

		urlCache.set(cacheKey, baseUrl);

		return baseUrl;
	} catch (error) {
		logImageError(path, `URL generation error: ${error}`);
		return '';
	}
}

export function getOptimizedAvatarUrl(path: string, size: number): string {
	try {
		// If it's a static avatar or avatar-default, return as-is (no Supabase transformation)
		if (
			!path ||
			path === 'avatar-default.svg' ||
			path.startsWith('/img/avatars/') ||
			path.startsWith('img/avatars/')
		) {
			return path.startsWith('/') ? path : '/' + path;
		}

		const cacheKey = `${path}-${size}x${size}`;
		if (urlCache.has(cacheKey)) {
			return urlCache.get(cacheKey)!;
		}

		const {
			data: { publicUrl: baseUrl }
		} = avatarBucket('avatars').getPublicUrl(path, {
			transform: {
				width: size,
				height: size, // Ensure square avatar
				format: 'origin',
				quality: 70, // Reduced quality for thumbnails
				resize: 'cover'
			}
		});

		urlCache.set(cacheKey, baseUrl);

		return baseUrl;
	} catch (error) {
		logImageError(path, `URL generation error: ${error}`);
		return '';
	}
}

export function getOptimizedImageSet(path: string): { src: string; srcset: string } {
	try {
		const sizes = [SIZES.mobile, SIZES.tablet, SIZES.desktop];

		const srcset = sizes
			.map((size) => {
				const url = getOptimizedImageUrl(path, size);
				const height = get16by9Height(size);
				return `${url} ${size}w ${height}h`;
			})
			.join(', ');

		// Use mobile size as default src
		const src = getOptimizedImageUrl(path, SIZES.mobile);

		return { src, srcset };
	} catch (error) {
		logImageError(path, `Image set generation error: ${error}`);
		return { src: '', srcset: '' };
	}
}

export function getBlurHash(path: string): string {
	try {
		const width = 20;
		const height = get16by9Height(width);
		const {
			data: { publicUrl: baseUrl }
		} = imageBucket('images').getPublicUrl(path, {
			transform: {
				width,
				height
			}
		});

		return baseUrl;
	} catch (error) {
		logImageError(path, `Blur hash generation error: ${error}`);
		return '';
	}
}
